<template>
  <div class="formbuilder-sidebar-editor">
    <div class="sidebar-editor-wrapper">
      <div
        class="close d-flex align-items-center justify-content-center"
        @click="onCloseModal"
      >
        <img src="@/assets/img/work-flow-icons/close.svg" />
      </div>
      <div class="sidebar-header">
        <h2>Element Bearbeiten</h2>
      </div>
      <div class="sidebar-content fancy-scroll" @scroll.passive="showScrollbar">
        <div class="element-attribute-wrapper">
          <form
            ref="sidebarElementEditorForm"
            @submit.prevent="handleSubmission"
          >
            <div v-if="isShowTypeSelectEl" class="form-item">
              <label for="Type">Type</label>
              <GeneralDropdown
                className="database-dropdown"
                class=""
                :disabled="!isUserHasAccess"
              >
                <template v-slot:selectFilter>
                  {{ getSelectedType }}
                </template>
                <template v-slot:optionData>
                  <b-dropdown-item
                    v-for="[key, value] in Object.entries(getTypeDropDownList)"
                    :key="key"
                    @click="changeElementType(key)"
                    class="group-items"
                    :class="{ selected: isSelectedType(key) }"
                  >
                    {{ value }}
                  </b-dropdown-item>
                </template>
              </GeneralDropdown>
            </div>
            <div class="form-item">
              <label for="Name">Name</label>
              <input
                id="Name"
                type="text"
                pattern="^[a-z0-9äöü]+$"
                v-model="element.name"
                onkeyup="this.value = this.value.toLowerCase()"
                @input="onTransformText"
                :class="{ error: isNameAlreadyExist }"
                required
              />
            </div>
            <div class="form-item" v-if="element.type !== 'resultframe'">
              <label for="Bezeichnung"> Bezeichnung </label>
              <textarea
                v-if="element.type === 'textblock'"
                id="Bezeichnung"
                v-model="element.label"
              />
              <input
                v-else
                id="Bezeichnung"
                type="text"
                v-model="element.label"
              />
            </div>
            <div class="form-item" v-if="element.type === 'audioinput'">
              <label for="audio-target-input"
                >Mit einem anderen Element verknüpfen, um Text
                hinzuzufügen.</label
              >
              <select
                id="audio-target-input"
                v-model="element.settings.targetElementName"
                :required="audioTargetElements.length > 0"
              >
                <option value="" disabled>Zielelement auswählen</option>
                <option
                  v-if="audioTargetElements.length === 0"
                  value=""
                  disabled
                >
                  Kein Text- oder Textbereichselement verfügbar.
                </option>
                <option
                  v-for="target in audioTargetElements"
                  :key="target.id"
                  :value="target.name"
                >
                  {{ target.name }}
                </option>
              </select>
            </div>
            <div class="form-item" v-if="element.type === 'wrapper'">
              <label for="domTagInput"> Tag </label>
              <input
                id="domTagInput"
                type="text"
                v-model="element.settings.tag"
                @blur="onDomTagInputBlur"
              />
            </div>
            <div class="form-item" v-if="element.type === 'wrapper'">
              <label> Attributes </label>
              <div class="add-more">
                <div
                  v-for="(attribute, index) in element.settings.attributes"
                  :key="index"
                  class="d-flex gap-2 align-items-center"
                >
                  <input
                    id="key"
                    type="text"
                    placeholder="class"
                    v-model="attribute.key"
                  />
                  <input
                    id="value"
                    type="text"
                    placeholder="bg-success"
                    v-model="attribute.value"
                  />
                  <div class="delete-option">
                    <img
                      src="@/assets/img/form-settings-trash.svg"
                      @click="removeOption(attribute)"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="add-more-option" v-if="element.type === 'wrapper'">
              <span
                class="add-more-option-button d-flex align-items-center justify-content-center"
                @click="addNewAttribute"
                :class="{ disabled: !isUserHasAccess }"
              >
                <img src="@/assets/img/plus-icon.svg" />
              </span>
            </div>
            <EditInputValueField
              v-if="isVisibleElement('inputElement')"
              :key="`editInput-${element.id}-${element.settings.required}`"
              v-model="element.value"
              :element="element"
              :isUserHasAccess="isUserHasAccess"
              @onFileChange="onFileChange"
            />

            <ToggleSwitchEditElement
              v-if="isVisibleElement('toggleSwitchElement')"
              :element="element"
            />
            <OptionCardEditElement
              v-else-if="isVisibleElement('optionCard')"
              :isUserHasAccess="isUserHasAccess"
              :element="element"
            />
            <div class="form-item" v-if="isShowIconSelection">
              <label for="description">Icon auswählen</label>
              <div class="form-element-icon-picker">
                <button
                  @click.prevent="toggleIconPicker"
                  :disable="!isUserHasAccess"
                >
                  Bild hinzufügen
                </button>
                <div
                  v-if="element.settings?.prefixIcon?.icon"
                  class="prefix-icon-preview position-relative d-flex align-items-center justify-content-center"
                >
                  <img
                    class="object-fit-contain mh-100 mw-100"
                    :src="element.settings.prefixIcon.icon"
                  />
                  <div @click="onRemovePrefixIcon" class="remove-icon">
                    <i class="fa fa-times"></i>
                  </div>
                </div>
                <IconPicker
                  v-if="isShowIconPicker"
                  v-click-outside="toggleIconPicker"
                  :displayIconPickerSections="['icon', 'custom']"
                  :element="element.settings.prefixIcon"
                  @updateUrl="onUpdateUrl"
                  @updateLinkType="onUpdateLinkType"
                />
              </div>
            </div>
            <b-button
              v-show="false"
              ref="submitBtnRef"
              type="submit"
              variant="primary"
              >Submit</b-button
            >
          </form>
        </div>
        <ElementAdditionalSetting
          ref="additionalSettingRef"
          v-if="isShowAdditionalSettings"
          :element="element"
          :isUserHasAccess="isUserHasAccess"
        />
        <ElementSwitchSetting
          v-if="isShowSwitchSettings"
          :element="element"
          :isUserHasAccess="isUserHasAccess"
        />
      </div>
    </div>
  </div>
</template>

<script>
import GeneralDropdown from '../core/general-dropdown.vue';
import EditInputValueField from './EditInputValueField.vue';
import ElementAdditionalSetting from './ElementAdditionalSetting.vue';
import ElementSwitchSetting from './ElementSwitchSetting.vue';
import OptionCardEditElement from './sidebarEditorElements/OptionCardEditElement.vue';
import IconPicker from '../widgets/IconPicker.vue';
import { debouncer } from '@/utils/helpers';
import { isValidOptionElement } from '@/utils/helpers/FormBuilderHelper';
import ToggleSwitchEditElement from '@/components/FormBuilder/sidebarEditorElements/ToggleSwitchEditElement';
export default {
  name: 'FormBuilderElementSidebarEditor',
  data() {
    return {
      nextElement: null,
      isShowIconPicker: false,
      typeDropdownItems: [
        {
          text: 'Text',
          audioinput: 'Audio',
          number: 'Zahl',
          formatted_number: 'formatierte Zahl',
          email: 'E-Mail-Adresse',
          textarea: 'Großer Text',
          hidden: 'hidden',
        },
        { date: 'Datum', 'datetime-local': 'Datum & Uhrzeit', time: 'Zeit' },
        {
          radio: 'Optionsfeld',
          radiooval: 'Optionskasten',
          radiocard: 'Optionskarten',
          select: 'Dropdown-Menü',
          checkbox: 'Kontrollkästchen',
          checkboxoval: 'Kontrollkasten',
          checkboxcard: 'Kontrollkarten',
        },
      ],
    };
  },
  props: {
    element: Object,
    elements: { type: Array, default: () => [] },
    existingNames: Array,
    reservedElementKeys: Array,
    typeList: { type: Array, default: () => [] },
    isUserHasAccess: { type: Boolean, default: true },
    isCreateCustomDB: Boolean,
  },
  methods: {
    onCloseModal() {
      this.formSubmit();
    },
    formSubmit(element = null) {
      this.nextElement = element;
      this.$refs.submitBtnRef.click();
    },
    handleSubmission() {
      if (!this.validateForm()) {
        return false;
      }
      if (this.nextElement) {
        this.$emit('changeElement', this.nextElement);
        return;
      }
      this.$emit('close');
    },
    validateForm() {
      if (!this.$refs.sidebarElementEditorForm.checkValidity()) {
        this.$refs.submitBtnRef.click();
        return false;
      }
      if (this.isNameAlreadyExist) {
        return false;
      } else if (
        ['checkbox', 'radio'].includes(this.element.type) &&
        !isValidOptionElement(this, this.element, true)
      ) {
        return false;
      }
      if (
        this.element?.settings?.appearance === 'card' &&
        this.element?.options?.length
      ) {
        const invalidCardOption = this.element.options.find(
          (op) =>
            op.isModalVisible && !op?.modalConfig?.modalLink?.trim()?.length
        );
        if (invalidCardOption) {
          // TODO add function for toaster
          // showToaster(
          //   this,
          //   `Bitte geben Sie eine Linkbeschriftung für die Option „${invalidCardOption.label}“ ein.`,
          //   'Fehlerhaft !',
          //   'danger'
          // );
          return;
        }
      }
      if (this.$refs.additionalSettingRef) {
        const isValidSetting = this.$refs.additionalSettingRef.formValidate();
        if (!isValidSetting) return false;
      }
      return true;
    },
    isSelectedType(type) {
      if (
        this.element.settings.appearance &&
        this.element.settings.appearance === 'card'
      ) {
        return type === 'radiocard'
          ? this.element.type === 'radio'
          : type === 'checkboxcard'
          ? this.element.type === 'checkbox'
          : false;
      } else if (
        this.element.settings.appearance &&
        this.element.settings.appearance === 'oval'
      ) {
        return type === 'radiooval'
          ? this.element.type === 'radio'
          : type === 'checkboxoval'
          ? this.element.type === 'checkbox'
          : false;
      }
      return type === this.element.type;
    },
    changeElementType(type) {
      if (this.getSelectedTypeGroupIndex === 0) {
        if (type === 'number' || type === 'formatted_number') {
          if (type === 'formatted_number') {
            this.element.settings = {
              ...this.element.settings,
              thSep: '.',
              decSep: ',',
              toFixed: 2,
            };
          }
          if (!String(this.element.value).match(/^[0-9]*\.?[0-9]+$/)) {
            this.element.value = 1;
          } else {
            this.element.value = Number(this.element.value);
          }
        } else if (type === 'range') {
          if (!String(this.element.value).match(/^(?:100|[1-9][0-9]?)$/)) {
            this.element.value = 15;
          } else {
            this.element.value = Number(this.element.value);
          }
          this.element.settings = {
            min: 1,
            max: 100,
            required: false,
          };
        } else if (type === 'hidden') {
          this.onRemovePrefixIcon();
        } else if (type === 'audioinput') {
          const audioDefaults = this.typeList.find(
            (elementType) => elementType.name === 'audioinput'
          )?.element;
          if (audioDefaults?.settings) {
            this.element.settings = {
              ...audioDefaults.settings,
              ...this.element.settings,
            };
          }
          if (!this.element.settings.targetElementName) {
            this.$set(this.element.settings, 'targetElementName', '');
          }
          if (typeof this.element.value !== 'string') {
            this.$set(this.element, 'value', '');
          }
        }

        if (
          type !== 'formatted_number' &&
          (this.element?.settings?.hasOwnProperty('toFixed') ||
            this.element?.settings?.hasOwnProperty('decSep') ||
            this.element?.settings?.hasOwnProperty('thSep'))
        ) {
          delete this.element.settings.toFixed;
          delete this.element.settings.decSep;
          delete this.element.settings.thSep;
        }
      } else if (this.getSelectedTypeGroupIndex === 1) {
        if (type === 'date') {
          this.element.value = this.element.value.substring(0, 10);
        } else if (type === 'datetime-local') {
          this.element.value =
            this.element.value && `${this.element.value}T01:00`;
        } else if (type === 'time') {
          const timePart = this.element.value.split('T')[1];
          this.element.value = timePart ? timePart.slice(0, 5) : '';
        }
      } else if (this.getSelectedTypeGroupIndex === 2) {
        const defaultElement = this.typeList.find((elementType) => {
          return elementType.name === type;
        })?.element;

        if (defaultElement) {
          const currentSettings = JSON.parse(
            JSON.stringify(this.element.settings)
          );
          if (defaultElement.settings) {
            for (const [key, value] of Object.entries(
              defaultElement.settings
            )) {
              if (!(key in currentSettings) || ['appearance'].includes(key)) {
                currentSettings[key] = value;
              }
            }
          }
          if (
            ['select', 'checkbox', 'radio'].includes(type) &&
            currentSettings.appearance
          ) {
            delete currentSettings.appearance;
          }
          this.$set(this.element, 'settings', currentSettings);

          for (const option of this.element.options || []) {
            if (['checkboxcard', 'radiocard'].includes(type)) {
              if (!option.hasOwnProperty('img')) {
                this.$set(option, 'img', '');
                this.$set(option, 'linkType', '');
              }
              if (!option.hasOwnProperty('isModalVisible')) {
                this.$set(option, 'isModalVisible', false);
              }
              if (!option.hasOwnProperty('modalConfig')) {
                this.$set(option, 'modalConfig', {
                  jsonContent: null,
                  htmlContent: null,
                  margin: 'small-margin',
                  modalLink: 'Mehr Infos',
                });
              }
            }
            if (['radio', 'select'].includes(this.element.type)) {
              if (option.value === this.element.value) {
                option.checked = true;
              } else {
                option.checked = false;
              }
            }
          }

          if (['radio', 'radiooval', 'radiocard', 'select'].includes(type)) {
            if (
              this.element.type === 'checkbox' &&
              this.element.options.length
            ) {
              const optionCheckedLength = this.element.options.filter(
                (option) => option.checked
              ).length;
              if (optionCheckedLength === 0 && this.element.options.length) {
                this.element.options[0].checked = true;
                this.element.value = this.element.options[0].value;
              } else if (optionCheckedLength >= 2) {
                this.element.options.forEach((option, index) => {
                  if (index === 0) {
                    option.checked = true;
                  } else {
                    option.checked = false;
                  }
                });
                this.element.value = this.element.options[0].value;
              } else {
                this.element.value = this.element.value[0];
              }
              if (
                this.element.settings.hasOwnProperty('min') ||
                this.element.settings.hasOwnProperty('max')
              ) {
                delete this.element.settings.min;
                delete this.element.settings.max;
              }
            }
            this.element.type = type === 'select' ? 'select' : 'radio';
          } else if (
            ['checkbox', 'checkboxcard', 'checkboxoval'].includes(type)
          ) {
            if (['radio', 'select'].includes(this.element.type)) {
              this.element.settings = {
                ...this.element.settings,
                min: this.element.settings.required ? 1 : 0,
                max: this.element.settings.required
                  ? this.element.options.length
                  : 0,
              };

              this.element.value = this.element.value
                ? [this.element.value]
                : [];
            }
            this.element.type = 'checkbox';
          }

          this.$forceUpdate();
        }
        return;
      }
      this.element.type = type;
    },
    onTransformText() {
      this.element.name = this.element.name.toLowerCase();
    },
    onFileChange() {
      this.$emit('onFileChange', this.element);
    },
    toggleIconPicker() {
      this.isShowIconPicker = !this.isShowIconPicker;
    },
    onUpdateUrl(icon) {
      this.$set(this.element.settings.prefixIcon, 'icon', icon);
    },
    onUpdateLinkType(linkType) {
      this.$set(this.element.settings.prefixIcon, 'linkType', linkType);
    },
    onRemovePrefixIcon() {
      this.$set(this.element.settings, 'prefixIcon', {
        icon: '',
        linkType: '',
      });
    },
    isVisibleElement(elementName) {
      if (elementName === 'inputElement') {
        return [
          'text',
          'number',
          'formatted_number',
          'email',
          'file',
          'date',
          'time',
          'datetime-local',
          'hidden',
          'textarea',
          'html',
          'model',
        ].includes(this.element.type);
      } else if (elementName === 'optionCard') {
        return ['radio', 'checkbox', 'select'].includes(this.element.type);
      } else if (elementName == 'toggleSwitchElement') {
        return this.element.type == 'toggleswitch';
      }
    },
    showScrollbar(event) {
      event.target.classList.add('show-scrollbar');
      debouncer(
        () => {
          event.target.classList.remove('show-scrollbar');
        },
        300,
        this,
        'removeScrollClass'
      );
    },
    onDomTagInputBlur() {
      if (this.element?.settings?.tag?.trim() === '') {
        this.element.settings.tag = 'div';
      }
    },
    addNewAttribute() {
      const attrs = this.element?.settings?.attributes || [];
      attrs.push({
        key: '',
        value: '',
      });
      this.$set(this.element.settings, 'attributes', attrs);
    },
    removeOption(attribute) {
      if (!this.isUserHasAccess) return;
      if (this.element.settings.attributes.length > 0) {
        this.element.settings.attributes =
          this.element.settings.attributes.filter((el) => {
            return el != attribute;
          });
      }
    },
  },
  computed: {
    getTypeDropDownList() {
      if (
        this.element.settings.appearance &&
        this.element.settings.appearance === 'card'
      ) {
        return this.typeDropdownItems[2];
      }
      return this.typeDropdownItems.find((group) =>
        Object.keys(group).includes(this.element.type)
      );
    },
    getSelectedTypeGroupIndex() {
      if (
        this.element.settings.appearance &&
        this.element.settings.appearance === 'card'
      ) {
        return 2;
      }
      return this.typeDropdownItems.findIndex((group) =>
        Object.keys(group).includes(this.element.type)
      );
    },
    getSelectedType() {
      return this.element.type
        ? this.element.settings.appearance === 'card'
          ? this.element.type === 'radio'
            ? this.getTypeDropDownList['radiocard']
            : this.getTypeDropDownList['checkboxcard']
          : this.element.settings.appearance === 'oval'
          ? this.element.type === 'radio'
            ? this.getTypeDropDownList['radiooval']
            : this.getTypeDropDownList['checkboxoval']
          : this.getTypeDropDownList[this.element.type]
        : 'select';
    },
    isShowTypeSelectEl() {
      return !!(
        this.getTypeDropDownList &&
        Object.keys(this.getTypeDropDownList)?.length &&
        this.isUserHasAccess
      );
    },
    isShowIconSelection() {
      return ![
        'hidden',
        'radio',
        'togglelist',
        'checkbox',
        'html',
        'select',
        'textblock',
        'model',
        'textarea',
        'audioinput',
        'wrapper',
        'resultframe',
      ].includes(this.element.type);
    },
    isNameAlreadyExist() {
      return (
        this.existingNames
          .concat(this.reservedElementKeys)
          .filter((key) => key === this.element.name).length > 1
      );
    },
    isShowSwitchSettings() {
      return !['title', 'togglelist', 'textblock', 'wrapper', 'resultframe'].includes(
        this.element.type
      );
    },
    isShowAdditionalSettings() {
      return (
        !this.element.settings.layoutItem &&
        this.element.type !== 'togglelist' &&
        this.element.type !== 'wrapper' &&
        this.element.type !== 'resultframe'
      );
    },
    audioTargetElements() {
      return (this.elements || []).filter(
        (el) =>
          el.id !== this.element?.id &&
          ['text', 'textarea'].includes(el.type) &&
          !el?.settings?.layoutItem &&
          !!el?.name
      );
    },
  },
  components: {
    EditInputValueField,
    GeneralDropdown,
    ElementAdditionalSetting,
    ElementSwitchSetting,
    IconPicker,
    OptionCardEditElement,
    ToggleSwitchEditElement,
  },
};
</script>

<style scoped>
.formbuilder-sidebar-editor {
  height: 100%;
  width: 395px;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.formbuilder-sidebar-editor .sidebar-editor-wrapper {
  text-align: left;
  padding: 54px 28px;
  border-left: 1px solid #dee3e9;
  border-radius: 0px 19px 19px 0px;
  background: #ffffff;
  display: block;
  text-align: left;
  font-size: 12px;
  line-height: 17px;
  color: #5f697a;
  height: 100%;
  width: 100%;
  position: relative;
}
.formbuilder-sidebar-editor .sidebar-editor-wrapper .close {
  position: absolute;
  top: 32px;
  right: 36px;
  width: 25px;
  height: 25px;
  cursor: pointer;
}
.formbuilder-sidebar-editor .sidebar-editor-wrapper .close img {
  width: 14px;
  height: 14px;
}
.formbuilder-sidebar-editor .sidebar-editor-wrapper .close:hover {
  opacity: 0.8;
}

.formbuilder-sidebar-editor .sidebar-content {
  height: calc(100dvh - 245px);
  overflow: auto;
  padding-right: 5px;
}
.formbuilder-sidebar-editor .sidebar-content .element-attribute-wrapper {
  padding-bottom: 20px;
}
.formbuilder-sidebar-editor .sidebar-header h2 {
  margin: 0;
  font-family: 'OpenSans-Regular';
  letter-spacing: -0.02em;
  text-align: center;
  text-align: center;
  font-size: 30px;
  color: #464d5d;
  font-weight: 400;
  line-height: 36px;
  text-rendering: optimizelegibility;
  padding-bottom: 25px;
}

.formbuilder-sidebar-editor form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.formbuilder-sidebar-editor .form-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.formbuilder-sidebar-editor .form-item .add-more {
  max-height: calc(100vh - 565px);
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.formbuilder-sidebar-editor .add-more-option {
  cursor: pointer;
}

.formbuilder-sidebar-editor .form-item .delete-option:hover img {
  opacity: 0.8;
  cursor: pointer;
}

.formbuilder-sidebar-editor ::v-deep .database-dropdown,
.formbuilder-sidebar-editor ::v-deep .database-dropdown .dropdown-menu {
  width: 100%;
}

.formbuilder-sidebar-editor ::v-deep .database-dropdown .dropdown-menu li a {
  overflow: hidden;

  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
}
.formbuilder-sidebar-editor ::v-deep .database-dropdown .selected a {
  background: #f0f0f0 !important;
}
.formbuilder-sidebar-editor
  ::v-deep
  .database-dropdown
  .database-dropdown-value-container {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-all;
  text-align: left;
}
.formbuilder-sidebar-editor ::v-deep .database-dropdown .customDropdown {
  justify-content: space-between;
  box-shadow: none;
}

.formbuilder-sidebar-editor .form-item label {
  font-size: 13px;
  font-family: Open Sans, open-sans, sans-serif;
  color: #464d5d;
}
.formbuilder-sidebar-editor .form-item input,
.formbuilder-sidebar-editor .form-item select,
.formbuilder-sidebar-editor .form-item textarea,
.formbuilder-sidebar-editor ::v-deep .database-dropdown .customDropdown {
  padding: 9px 12px;
  border: 1px solid #dee3e9;
  color: #464d5d !important;
  background: #ffffff;
  border-radius: 2px;
  width: 100%;
  max-width: 100%;
  height: auto;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  cursor: default;
}
.formbuilder-sidebar-editor .form-item textarea {
  height: 200px;
  resize: none;
  overflow: scroll;
}
.formbuilder-sidebar-editor .form-item input.error {
  background: #ff8790;
}

.formbuilder-sidebar-editor .form-element-icon-picker {
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 15px;
}
.formbuilder-sidebar-editor
  .form-element-icon-picker
  ::v-deep
  .icon-picker-widget {
  top: 50px;
  right: 30px;
}
.formbuilder-sidebar-editor .form-element-icon-picker .prefix-icon-preview {
  background: #ededed;
  border-radius: 6px;
  width: 45px;
  height: 45px;
}

.formbuilder-sidebar-editor
  .form-element-icon-picker
  .prefix-icon-preview
  .remove-icon {
  position: absolute;
  top: -5px;
  right: -5px;
  background: black;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  text-align: center;
  color: white;
  cursor: pointer;
  display: none;
  transition: 0.5s ease all;
}
.formbuilder-sidebar-editor
  .form-element-icon-picker
  .prefix-icon-preview:hover
  .remove-icon {
  display: block;
}
.formbuilder-sidebar-editor .form-element-icon-picker button {
  width: fit-content;
  height: 45px;
  margin-bottom: 0;
  cursor: pointer;
  color: #ffffff;
  font-size: 16px;
  font-weight: 500;
  background-color: #000000;
  border: none;
  border-radius: 8px;
  padding: 0 30px;
}
.formbuilder-sidebar-editor .fancy-scroll::-webkit-scrollbar-thumb {
  display: none;
}

.formbuilder-sidebar-editor .show-scrollbar::-webkit-scrollbar-thumb {
  display: block;
}
</style>
